import React, { useState, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import ReCAPTCHA from "react-google-recaptcha";
// Using public folder image

function AffiliateSignup() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { carrierPortal } = theme;
  
  const getNavLinkClass = (path) => {
    const isActive = location.pathname === path;
    return isActive 
      ? "text-orange-500 hover:text-orange-400 transition font-semibold"
      : "text-gray-200 hover:text-gray-300 transition";
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/#contact');
    setIsNavOpen(false);
  };
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    businessType: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    taxId: '',
    referralMethod: '',
    expectedReferrals: '',
    experience: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const errorRef = useRef(null);

  const formatPhoneNumber = (input) => {
    // Remove all non-numeric characters
    const cleaned = input.replace(/\D/g, "");
    // Apply formatting (123) 456-7890
    if (cleaned.length <= 3) {
      return `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle phone number input change with formatting
  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formattedPhone
    }));
  };

  // Validate phone number format before submission
  const isValidPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, ""); // Remove non-numeric chars
    return cleaned.length === 10; // Ensure it's exactly 10 digits
  };

  // Handle reCAPTCHA verification
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setError('');

    // Validate reCAPTCHA
    if (!captchaValue) {
      setError("Please complete the reCAPTCHA verification.");
      setIsSubmitting(false);
      return;
    }

    // Validate phone number
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      setError("Please enter a valid phone number in the format: (123) 456-7890");
      setIsSubmitting(false);
      return;
    }

    const subDomain = window.location.hostname.split(".")[0];

    try {
      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          action: 'handlePartnerApplicationSubmission',
          payload: {
            ...formData,
            userId: 'anonymous_' + Date.now(), // For anonymous submissions
            tenantId: 'Go-Transport-LLC-o5bl2', // Default tenant
            recaptchaToken: captchaValue,
            subdomain: subDomain,
            timestamp: new Date().toISOString(),
            source: 'web_signup',
            ipAddress: null, // Will be captured server-side
            userAgent: navigator.userAgent
          }
        })
      });

      clearTimeout(timeoutId); // Clear timeout if request completes

      const data = await response.json();

      if (data.success) {
        setSubmitStatus('success');
        setSuccessData({
          message: data.message,
          affiliateId: data.affiliateId,
          referralCode: data.referralCode
        });
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          businessType: '',
          businessAddress: '',
          businessCity: '',
          businessState: '',
          businessZip: '',
          taxId: '',
          referralMethod: '',
          expectedReferrals: '',
          experience: '',
          comments: ''
        });
        setCaptchaValue(null);
      } else {
        throw new Error(data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Affiliate signup error:', error);
      
      if (error.name === 'AbortError') {
        setError('Request timed out. Your application may have been submitted successfully. Please check your email for confirmation or contact support.');
      } else if (error.message && error.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(error.message || 'An error occurred. Please try again later.');
      }
      
      setSubmitStatus('error');
      
      // Scroll to error message
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col">
        {/* Header - Same as loginDemo */}
        <header className="top-0 z-50 bg-gray-900 shadow-md">
          <div className="container mx-auto px-6 py-2 flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img
                src="/logos/fln_logo_white.png"
                alt="FuelLink Logo"
                className="h-20 w-auto object-contain"
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-6">
              <Link to="/" className={getNavLinkClass('/')}>
                Home
              </Link>
              <Link to="/locationfinderdemo" className={getNavLinkClass('/locationfinderdemo')}>
                Network
              </Link>
              <a href="/#contact" onClick={handleContactClick} className="text-gray-200 hover:text-gray-300 transition">
                Contact
              </a>
              <Link to="/affiliate-signup" className={getNavLinkClass('/affiliate-signup')}>
                Partners
              </Link>
              <Link to="/registration" className={getNavLinkClass('/registration')}>
                Sign Up
              </Link>
            </nav>

            {/* Hamburger for mobile */}
            <div className="md:hidden">
              <button
                onClick={() => setIsNavOpen(!isNavOpen)}
                className="text-gray-200 focus:outline-none"
              >
                <FontAwesomeIcon icon={faBars} className="text-orange-500 text-4xl" />
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          <nav
            className={`md:hidden transition-all duration-300 bg-gray-900 ${
              isNavOpen ? 'block' : 'hidden'
            }`}
          >
            <div className="px-6 py-2">
              <Link
                to="/"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/locationfinderdemo"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/locationfinderdemo').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Network
              </Link>
              <a
                href="/#contact"
                className="block text-gray-200 hover:bg-gray-700 rounded py-2"
                onClick={handleContactClick}
              >
                Contact
              </a>
              <Link
                to="/affiliate-signup"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/affiliate-signup').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Partners
              </Link>
              <Link
                to="/registration"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/registration').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          </nav>
        </header>

        {/* Hero Section with Gradient Background */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100">
          <div className="mx-auto px-6 md:px-24 py-16">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Left Content */}
                <div>
                  <h1 className="text-5xl font-bold mb-6 text-white leading-tight">
                    Join the FLN Partner Network
                  </h1>
                  <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                    Earn ongoing commissions on every gallon purchased by truckers and fleet operators 
                    you refer to our industry-leading fuel card program.
                  </p>
                  
                  {/* Benefits Cards */}
                  <div className="space-y-4">
                    <div className="flex items-center bg-gray-800/40 rounded-lg p-4 border border-gray-700/50">
                      <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-lg">Gallon-Based Commissions</h3>
                        <p className="text-gray-400 text-sm">Earn on every in-network gallon purchased by your referrals</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center bg-gray-800/40 rounded-lg p-4 border border-gray-700/50">
                      <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-lg">Real-Time Tracking</h3>
                        <p className="text-gray-400 text-sm">Monitor referrals and earnings instantly</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center bg-gray-800/40 rounded-lg p-4 border border-gray-700/50">
                      <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-lg">4,500+ Locations</h3>
                        <p className="text-gray-400 text-sm">Industry-leading fuel network coverage</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Image */}
                <div className="lg:order-last">
                  <div className="relative">
                    <img
                      src="/images/fln_affiliate.jpg"
                      alt="FLN Affiliate Program"
                      className="w-full h-auto rounded-2xl shadow-2xl"
                    />
                    {/* Subtle overlay for better blending */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-transparent via-transparent to-gray-900/20"></div>
                    {/* Edge fade effects */}
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-900/40 to-transparent rounded-b-2xl"></div>
                    <div className="absolute top-0 right-0 bottom-0 w-16 bg-gradient-to-l from-gray-900/20 to-transparent rounded-r-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Application Form Section */}
        <div className="bg-gradient-to-b from-gray-900 via-gray-700 to-gray-900 text-gray-100 py-16">
          <div className="mx-auto px-6 max-w-4xl">
            <div className="bg-gray-800/50 rounded-lg shadow-md p-8">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Partner Application</h2>
          
              {submitStatus === 'error' && (
                <div ref={errorRef} className="bg-red-600/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-6">
                  {error || 'There was an error submitting your application. Please try again or contact support.'}
                </div>
              )}

              {error && !submitStatus && (
                <div ref={errorRef} className="bg-red-600/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-6">
                  {error}
                </div>
              )}

              {submitStatus === 'success' && successData && (
                <div className="bg-green-600/20 border border-green-500 text-green-300 rounded-lg p-6 text-center">
                  <div className="mb-4">
                    <svg className="w-12 h-12 text-green-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl font-bold text-green-300 mb-2">Application Submitted Successfully!</h3>
                    <p className="text-green-200 mb-4">{successData.message}</p>
                  </div>
                  
                  <div className="space-y-2 text-green-200 text-sm mb-6">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>Email confirmation sent to your inbox</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>We'll review your application within 2-3 business days</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>Our partnership team will contact you directly</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setSubmitStatus(null);
                      setSuccessData(null);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
                  >
                    Submit Another Application
                  </button>
                </div>
              )}

              {submitStatus !== 'success' && (
                <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="(123) 456-7890"
                      required
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Business Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company/Organization *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Business Type
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Select business type</option>
                      <option value="Transportation Company">Transportation Company</option>
                      <option value="Fleet Manager">Fleet Manager</option>
                      <option value="Owner Operator">Owner Operator</option>
                      <option value="Fuel Consultant">Fuel Consultant</option>
                      <option value="Industry Blogger/Influencer">Industry Blogger/Influencer</option>
                      <option value="Equipment Dealer">Equipment Dealer</option>
                      <option value="Insurance Agent">Insurance Agent</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tax ID (Optional)
                    </label>
                    <input
                      type="text"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                      placeholder="EIN or SSN"
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Business Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Business Address
                  </label>
                  <input
                    type="text"
                    name="businessAddress"
                    value={formData.businessAddress}
                    onChange={handleInputChange}
                    placeholder="Street Address"
                    className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="businessCity"
                      value={formData.businessCity}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      name="businessState"
                      value={formData.businessState}
                      onChange={handleInputChange}
                      placeholder="e.g., CA"
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      name="businessZip"
                      value={formData.businessZip}
                      onChange={handleInputChange}
                      placeholder="12345"
                      className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Marketing Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    How do you plan to promote FLN? *
                  </label>
                  <select
                    name="referralMethod"
                    value={formData.referralMethod}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select a method</option>
                    <option value="social_media">Social Media</option>
                    <option value="website_blog">Website/Blog</option>
                    <option value="email_marketing">Email Marketing</option>
                    <option value="direct_referrals">Direct Referrals</option>
                    <option value="industry_events">Industry Events</option>
                    <option value="youtube_content">YouTube/Video Content</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Expected monthly referrals *
                  </label>
                  <select
                    name="expectedReferrals"
                    value={formData.expectedReferrals}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select range</option>
                    <option value="1-5">1-5 referrals</option>
                    <option value="6-15">6-15 referrals</option>
                    <option value="16-30">16-30 referrals</option>
                    <option value="31-50">31-50 referrals</option>
                    <option value="50+">50+ referrals</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Experience with affiliate marketing
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select experience level</option>
                    <option value="new">New to affiliate marketing</option>
                    <option value="some">Some experience (1-2 years)</option>
                    <option value="experienced">Experienced (3+ years)</option>
                    <option value="expert">Expert (5+ years)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Additional Comments
                  </label>
                  <textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell us more about your marketing strategy or any questions you have..."
                    className="w-full p-3 border border-gray-600 rounded-md bg-gray-700 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-center">
                  <ReCAPTCHA 
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    theme="dark"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting || !captchaValue}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 text-white font-semibold py-3 px-6 rounded-md transition duration-300 flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        Submitting Application...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                  <p className="text-sm text-gray-400 mt-2 text-center">
                    By submitting this form, you agree to our affiliate terms and conditions.
                  </p>
                </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Same as loginDemo */}
        <footer className="bg-black text-gray-300 py-6">
          <div className="container mx-auto px-6 text-center">
            &copy; {new Date().getFullYear()} FLN Fuel Network. All rights reserved.
          </div>
        </footer>
      </div>
    </>
  );
}

export default AffiliateSignup;